import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";

export default function NewsletterSignup({ source = "blog" }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/newsletter/subscribe", { email, source });
      setDone(true);
      toast.success(res.data.message || "You're subscribed!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-6 lg:px-12 py-12" data-testid="newsletter-signup">
      <div className="relative overflow-hidden rounded-[2rem] bg-charcoal-900 text-sand-100 p-10 lg:p-14">
        <div className="absolute inset-0 pattern-talavera opacity-[0.06]" aria-hidden="true" />
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta text-white">
              <Mail size={22} strokeWidth={1.5} />
            </span>
            <h2 className="mt-5 text-2xl sm:text-3xl font-light tracking-tight">Move-to-Mexico tips, in your inbox</h2>
            <p className="mt-3 text-sand-100/75 leading-relaxed max-w-md">
              Honest guidance on relocating to Mérida and beyond — no spam, no pressure. Just useful, calm advice when you need it.
            </p>
          </div>
          {done ? (
            <div data-testid="newsletter-success" className="flex items-center gap-3 rounded-2xl bg-white/[0.06] border border-white/10 p-6 backdrop-blur-sm">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage text-white">
                <Check size={20} strokeWidth={2} />
              </span>
              <p className="text-sand-100">You're on the list — thank you! Keep an eye on your inbox.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                data-testid="newsletter-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 rounded-full bg-white/10 border border-white/20 px-5 py-3 text-sand-100 placeholder:text-sand-100/40 focus:outline-none focus:ring-2 focus:ring-terracotta/60"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="rounded-full bg-terracotta px-7 py-3 font-medium text-white hover:bg-terracotta-hover transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {loading ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
